import React, { useState, useEffect } from 'react';
import { api } from './api/api';

// دالة مساعدة لجلب النص من عناصر XML (موجودة في ManifestScreen.jsx)
const parseIdArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  if (typeof value === 'string') { try { return JSON.parse(value); } catch { return [value]; } }
  return [value];
};

// Helper to normalize type string for comparison
const normalizeType = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

// Helper to find service by type and weight (copied from BOLIssuanceScreen.jsx logic)
const findServiceByTypeAndWeight = (serviceType, weight, servicesList) => {
  if (!serviceType) return null;
  const normalizedType = normalizeType(serviceType);
  const matchingType = servicesList.filter((s) => normalizeType(s.type) === normalizedType);
  if (matchingType.length === 0) return null;

  const sorted = matchingType
    .map((s) => ({
      ...s,
      rangeFrom: Number(s.range_from ?? s.rangeFrom ?? 0),
      rangeTo: Number(s.range_to ?? s.rangeTo ?? Infinity)
    }))
    .sort((a, b) => a.rangeFrom - b.rangeFrom);

  const byWeight = sorted.find((s) => {
    const from = Number.isFinite(s.rangeFrom) ? s.rangeFrom : 0;
    const to = Number.isFinite(s.rangeTo) ? s.rangeTo : Infinity;
    return weight >= from && weight <= to;
  });

  return byWeight || sorted[0] || null;
};

const OperatorApplicationForm = () => {
  const [form, setForm] = useState({
    id: null, // تتبع المعرف للتحديث
    ownerOperator: '',
    aircraftRegistration: '',
    declaredTotalAwbs: '',
    customsOfficeCode: '',
    voyageFlightNo: '',
    portOfLoading: '',
    portOfDischarge: '',
    // BOL fields from the original form
    bolReference: '',
    bolNature: '',
    bolTypeCode: '740',
    notifyName: '',
    notifyAddress: '',
    customerName: '',
    numberOfPackages: '',
    packageTypeCode: '',
    grossMass: '',
    shippingMarks: '',
    goodsDescription: '',
    cargoTypes: [], // New: Array to hold selected cargo types
    services: [], // New: Array to hold selected services
    packingItems: [], // New: Array to hold selected packing items
    buildUpItems: [], // New: Array to hold selected build-up items
    homes: [],
    day: false
  });
  const [submittedBols, setSubmittedBols] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    fetchLists(); // Fetch lists on initial mount
    loadSubmittedBols();

    const handleFocus = () => {
      // عند العودة للشاشة، أعد تحميل القائمة
      // هذا يضمن تحديث حالة البوالص المعروضة (مثلاً، إذا تم إرجاعها للمراجعة)
      // وأيضاً تحديث القوائم المنسدلة للخدمات وغيرها
      fetchLists(); 
      loadSubmittedBols();
    };

    window.addEventListener('focus', handleFocus);
    const interval = setInterval(loadSubmittedBols, 20000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, []);

  // New: State variables for lists
  const [servicesList, setServicesList] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [buildUpItems, setBuildUpItems] = useState([]);
  const [cargoTypesList, setCargoTypesList] = useState([]); // Renamed to avoid conflict with form.cargoTypes

  // New: Fetch all necessary lists from API
  const fetchLists = async () => {
    try {
      const [servicesRes, packingRes, buildUpRes, cargoTypesRes] = await Promise.all([
        api.get('/services'),
        api.get('/packing'),
        api.get('/build-up'),
        api.get('/cargo-types')
      ]);
      setServicesList(Array.isArray(servicesRes) ? servicesRes : []);
      setPackingItems(Array.isArray(packingRes) ? packingRes : []);
      setBuildUpItems(Array.isArray(buildUpRes) ? buildUpRes : []);
      setCargoTypesList(Array.isArray(cargoTypesRes) ? cargoTypesRes : []);
    } catch (err) { console.error('Error fetching lists:', err); }
  };

const loadSubmittedBols = async () => {
    try {
      const response = await api.get('/bols/pending/returned');
      const arr = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.rows)
            ? response.rows
            : [];

      // إظهار فقط البوالص التي تم تعليمها بأنها مرجعة للمراجعة
      setSubmittedBols(arr.filter(b => b.returned_for_review == 1));
    } catch (err) {
      console.error('Error fetching pending BOLs:', err);
      setSubmittedBols([]);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Helper to find service by type (for operator, no weight classification needed here)
  const findServiceByType = (type) => servicesList.find(s => s.type === type);

  // New: Empty House BOL template
  const emptyHouseBol = {
    bolReference: '',
    bolNature: '23',
    bolTypeCode: '740',
    numberOfPackages: '0',
    packageTypeCode: 'PK',
    grossMass: '0',
    shippingMarks: 'A',
    goodsDescription: '',
    services: [],
    packingItems: [],
    buildUpItems: []
  };

  const addHouseBol = () => {
    const newHouse = { ...emptyHouseBol, tempId: `house_${Date.now()}_${Math.random()}` };
    setForm(prev => ({ ...prev, homes: [...prev.homes, newHouse], bolTypeCode: '741' }));
  };

  const removeHouseBol = (tempId) => {
    const updatedHomes = form.homes.filter(h => h.tempId !== tempId);
    setForm(prev => ({ ...prev, homes: updatedHomes, bolTypeCode: updatedHomes.length > 0 ? '741' : '740' }));
  };
  
  const uniqueServiceTypes = [...new Set(servicesList.map(s => s.type).filter(Boolean))];

  const handleAutoServiceSelect = (type) => {
    const weight = Number(form.grossMass) || 0;
    if (weight <= 0) {
      alert('Please enter Gross Mass (Weight) first to determine the correct classification.');
      return;
    }
    const matched = findServiceByTypeAndWeight(type, weight, servicesList);
    if (matched) {
      if (!form.services.some(s => s.type === matched.type)) {
        setForm(prev => ({ ...prev, services: [...prev.services, { ...matched, showPacking: false, selectedPackingId: null }] }));
      } else {
        alert('This service type is already selected.');
      }
    }
  };

  // دالة الإضافة التلقائية للخدمات داخل بوليصة هاوس محددة
  const handleHouseAutoServiceSelect = (houseIdx, type) => {
    const house = form.homes[houseIdx];
    const weight = Number(house.grossMass) || 0;
    if (weight <= 0) return alert('Please enter Weight for this House AWB first.');

    const matched = findServiceByTypeAndWeight(type, weight, servicesList);
    if (matched) {
      const newHomes = [...form.homes];
      if (!newHomes[houseIdx].services.some(s => s.type === matched.type)) {
        newHomes[houseIdx].services = [...newHomes[houseIdx].services, { ...matched, showPacking: false, selectedPackingId: null }];
        setForm(prev => ({ ...prev, homes: newHomes }));
      } else {
        alert('This service type is already selected for this House AWB.');
      }
    }
  };

  // Auto-calculate master packages/weight from house BOLs
  useEffect(() => {
    if (form.homes.length > 0) {
      const totalPkgs = form.homes.reduce((sum, h) => sum + (Number(h.numberOfPackages) || 0), 0);
      const totalWeight = form.homes.reduce((sum, h) => sum + (Number(h.grossMass) || 0), 0);
      setForm(prev => ({ ...prev, numberOfPackages: totalPkgs.toString(), grossMass: totalWeight.toString(), bolTypeCode: '741' }));
    } else if (form.homes.length === 0 && form.bolTypeCode === '741') {
      setForm(prev => ({ ...prev, bolTypeCode: '740' }));
    }
  }, [form.homes, form.bolTypeCode]);

  const submitApplication = async () => {
    // Validation
    if (!form.ownerOperator) return alert('Please enter Owner/Operator');
    if (!form.aircraftRegistration) return alert('Please enter Aircraft Registration');
    // if (!form.customsOfficeCode) return alert('Please enter Customs Office Code');
    // if (!form.voyageFlightNo) return alert('Please enter Voyage/Flight No.');
    if (!form.portOfLoading) return alert('Please enter Port of Loading');
    if (!form.portOfDischarge) return alert('Please enter Port of Discharge');
    if (!form.bolReference) return alert('Please enter AWB Reference');
    if (form.cargoTypes.length === 0) return alert('Please select at least one Cargo Type');
    if (form.services.length === 0) return alert('Please select at least one Service for the Master AWB');
    if (form.packingItems.length === 0) return alert('Please select at least one Packing Item');
    if (form.buildUpItems.length === 0) return alert('Please select at least one Build-up Item');
    if (!form.grossMass || !form.numberOfPackages) return alert('Please enter Weight and Packages');

    try {
      const isUpdate = !!form.id;
      const payload = {
        owner_operator: form.ownerOperator,
        aircraft_registration: form.aircraftRegistration,
        declared_total_awbs: form.declaredTotalAwbs,
        customs_office_code: form.customsOfficeCode,
        voyage_flight_no: form.voyageFlightNo,
        port_of_loading: form.portOfLoading,
        port_of_discharge: form.portOfDischarge,
        bol_reference: form.bolReference,
        bol_nature: form.bolNature || '23', // New: Empty House BOL template
        bol_type_code: form.homes.length > 0 ? '741' : '740',
        customer_name: form.customerName,
        number_of_packages: form.numberOfPackages,
        package_type_code: form.packageTypeCode || 'PK',
        gross_mass: form.grossMass,
        shipping_marks: form.shippingMarks,
        goods_description: form.goodsDescription,
        status: 'pending_services',
        cargo_types: form.cargoTypes, // New
        service_ids: form.services.map(s => s.id), // New
        packing_item_ids: form.packingItems.map(p => p.id), // New
        build_up_item_ids: form.buildUpItems.map(b => b.id), // New
        homes: form.homes.map(h => ({ // Map house BOLs to snake_case for backend
          bol_reference: h.bolReference,
          bol_nature: h.bolNature,
          bol_type_code: h.bolTypeCode,
          notify_name: h.notifyName,
          notify_address: h.notifyAddress,
          number_of_packages: h.numberOfPackages,
          package_type_code: h.packageTypeCode,
          gross_mass: h.grossMass,
          shipping_marks: h.shippingMarks,
          goods_description: h.goodsDescription,
          service_ids: h.services.map(s => s.id),
          packing_item_ids: h.packingItems.map(p => p.id),
          build_up_item_ids: h.buildUpItems.map(b => b.id)
        }))
      };

            if (isUpdate) {
        payload.returned_for_review = 0;  // إزالة علم الإرجاع
        payload.status = 'pending_services';
        
        // ✅ استخدام PUT مع الـ ID
        await api.put(`/bols/${form.id}`, payload);
        alert('Application updated successfully ✅');
      } else {
        const created = await api.post('/bols/operator-submission', payload);
        alert('Application submitted successfully ✅');
      }
      
      // ✅ إعادة تحميل القائمة بعد الحفظ
      await loadSubmittedBols();
      
      // Reset form
      setForm({
        id: null,
        ownerOperator: '',
        aircraftRegistration: '',
        declaredTotalAwbs: '',
        customsOfficeCode: '',
        voyageFlightNo: '',
        portOfLoading: '',
        portOfDischarge: '',
        bolReference: '',
        bolNature: '',
        bolTypeCode: '740',
        customerName: '',
        numberOfPackages: '',
        packageTypeCode: '',
        grossMass: '',
        shippingMarks: '',
        goodsDescription: '',
        cargoTypes: [],
        services: [],
        packingItems: [],
        buildUpItems: [],
        homes: []
      });
    //   setIsSubmitted(true);
    //   setTimeout(() => setIsSubmitted(false), 3000);
    } catch (err) {
      console.error('Error submitting application:', err);
      alert('Error submitting application');
    }
  };

  // ✅ وظيفة جديدة لتحميل بوليصة مرتجعة للمراجعة في الفورم
  const editReturnedBol = (bol) => {
    // تحويل بيانات الهاوس من snake_case إلى camelCase
    const normalizedHomes = (bol.homes || []).map(h => ({
      id: h.id,
      tempId: h.tempId || h.id,
      bolReference: h.bol_reference || h.bolReference || '',
      bolNature: h.bol_nature || h.bolNature || '23',
      bolTypeCode: h.bol_type_code || h.bolTypeCode || '740',
      notifyName: h.notify_name || h.notifyName || '', // New: Empty House BOL template
      notifyAddress: h.notify_address || h.notifyAddress || '',
      numberOfPackages: h.number_of_packages || h.numberOfPackages || '',
      packageTypeCode: h.package_type_code || h.packageTypeCode || 'PK',
      grossMass: h.gross_mass || h.grossMass || '',
      shippingMarks: h.shipping_marks || h.shippingMarks || '',
      goodsDescription: h.goods_description || h.goodsDescription || '',
      services: parseIdArray(h.service_ids).map(id => servicesList.find(s => s.id == id)).filter(Boolean),
      packingItems: parseIdArray(h.packing_item_ids).map(id => packingItems.find(p => p.id == id)).filter(Boolean),
      buildUpItems: parseIdArray(h.build_up_item_ids).map(id => buildUpItems.find(b => b.id == id)).filter(Boolean)
    }));

    setForm({
      id: bol.id,
      ownerOperator: bol.owner_operator || '',
      aircraftRegistration: bol.aircraft_registration || '',
      declaredTotalAwbs: bol.declared_total_awbs || '',
      customsOfficeCode: bol.customs_office_code || '',
      voyageFlightNo: bol.voyage_flight_no || '',
      portOfLoading: bol.port_of_loading || '',
      portOfDischarge: bol.port_of_discharge || '',
      bolReference: bol.bol_reference || '',
      bolNature: bol.bol_nature || '23', // New: Empty House BOL template
      bolTypeCode: bol.bol_type_code || (normalizedHomes.length > 0 ? '741' : '740'),
      customerName: bol.customer_name || '',
      numberOfPackages: bol.number_of_packages || '',
      packageTypeCode: bol.package_type_code || 'PK',
      grossMass: bol.gross_mass || '',
      shippingMarks: bol.shipping_marks || '',
      goodsDescription: bol.goods_description || '',
      cargoTypes: parseIdArray(bol.cargo_types), // New
      services: parseIdArray(bol.service_ids).map(id => servicesList.find(s => s.id == id)).filter(Boolean), // New
      packingItems: parseIdArray(bol.packing_item_ids).map(id => packingItems.find(p => p.id == id)).filter(Boolean), // New
      buildUpItems: parseIdArray(bol.build_up_item_ids).map(id => buildUpItems.find(b => b.id == id)).filter(Boolean), // New
      homes: normalizedHomes,
      day: false
    });
    // التمرير لأعلى الصفحة
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="page">
      <h2>Operator Application Form - AWB/BOL Submission</h2>

      {isSubmitted && (
        <div style={{ 
          background: '#c8e6c9', 
          color: '#1b5e20', 
          padding: '15px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          border: '1px solid #81c784'
        }}>
          ✓ Application submitted successfully!
        </div>
      )}

      <div className="card">
        <h3>Flight/Voyage Information</h3>
        <div className="grid grid-auto">
          <div className="field">
            <label>Owner or Operator *</label>
            <input
              type="text"
              value={form.ownerOperator}
              onChange={(e) => handleFormChange('ownerOperator', e.target.value)}
              placeholder="Airline or Freight Forwarder Name"
            />
          </div>

          <div className="field">
            <label>Aircraft Registration *</label>
            <input
              type="text"
              value={form.aircraftRegistration}
              onChange={(e) => handleFormChange('aircraftRegistration', e.target.value)}
              placeholder="e.g., N123AA"
            />
          </div>

          <div className="field">
            <label>Declared Total AWBs</label>
            <input
              type="number"
              value={form.declaredTotalAwbs}
              onChange={(e) => handleFormChange('declaredTotalAwbs', e.target.value)}
              placeholder="Total number of AWBs"
            />
          </div>

          {/* <div className="field">
            <label>Customs Office Code *</label>
            <input
              type="text"
              value={form.customsOfficeCode}
              onChange={(e) => handleFormChange('customsOfficeCode', e.target.value)}
              placeholder="Port/Airport code"
            />
          </div> */}

          {/* <div className="field">
            <label>Voyage / Flight No. *</label>
            <input
              type="text"
              value={form.voyageFlightNo}
              onChange={(e) => handleFormChange('voyageFlightNo', e.target.value)}
              placeholder="Flight number or Voyage"
            />
          </div> */}

          <div className="field">
            <label>Port of Loading (Origin) *</label>
            <input
              type="text"
              value={form.portOfLoading}
              onChange={(e) => handleFormChange('portOfLoading', e.target.value)}
              placeholder="Loading port/airport"
            />
          </div>

          <div className="field">
            <label>Port of Discharge (Destination) *</label>
            <input
              type="text"
              value={form.portOfDischarge}
              onChange={(e) => handleFormChange('portOfDischarge', e.target.value)}
              placeholder="Discharge port/airport"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Bill of Lading (BOL) Information</h3>
        <div className="grid grid-auto">
          <div className="field">
            <label>BOL Reference *</label>
            <input
              type="text"
              value={form.bolReference}
              onChange={(e) => handleFormChange('bolReference', e.target.value)}
            />
          </div>

          <div className="field">
            <label>BOL Nature</label>
            <input
              type="text"
              value={form.bolNature}
              onChange={(e) => handleFormChange('bolNature', e.target.value)}
              placeholder="Default: 23"
            />
          </div>

          {/* <div className="field">
            <label>Notify Name</label>
            <input
              type="text"
              value={form.notifyName}
              onChange={(e) => handleFormChange('notifyName', e.target.value)}
            />
          </div> */}

          {/* <div className="field">
            <label>Notify Address</label>
            <input
              type="text"
              value={form.notifyAddress}
              onChange={(e) => handleFormChange('notifyAddress', e.target.value)}
            />
          </div> */}

          <div className="field">
            <label>Number of Packages *</label>
            <input
              type="number"
              value={form.numberOfPackages}
              onChange={(e) => handleFormChange('numberOfPackages', e.target.value)}
            />
          </div>

          <div className="field">
            <label>Package Type Code</label>
            <input
              type="text"
              value={form.packageTypeCode}
              onChange={(e) => handleFormChange('packageTypeCode', e.target.value)}
              placeholder="Default: PK"
            />
          </div>

          <div className="field">
            <label>Gross Mass (Weight in kg) *</label>
            <input
              type="number"
              value={form.grossMass}
              onChange={(e) => handleFormChange('grossMass', e.target.value)}
            />
          </div>

          {/* <div className="field">
            <label>Shipping Marks</label>
            <input
              type="text"
              value={form.shippingMarks}
              onChange={(e) => handleFormChange('shippingMarks', e.target.value)}
            />
          </div> */}

          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Goods Description</label>
            <input
              type="text"
              value={form.goodsDescription}
              onChange={(e) => handleFormChange('goodsDescription', e.target.value)}
            />
          </div>

          {/* New: Cargo Types Selection */}
          <div className="field">
            <label>Cargo Types *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {form.cargoTypes.map((type, idx) => (
                <span key={idx} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' }}>
                  {type}
                  <button
                    onClick={() => setForm(f => ({ ...f, cargoTypes: f.cargoTypes.filter(t => t !== type) }))}
                    style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                  >×</button>
                </span>
              ))}
            </div>
            <div className="select-field">
              <select value="" onChange={e => {
                const val = e.target.value;
                if (val && !form.cargoTypes.includes(val)) {
                  setForm(f => ({ ...f, cargoTypes: [...f.cargoTypes, val] }));
                }
              }}>
                <option value="">Select Cargo Type...</option>
                {cargoTypesList.filter(ct => !form.cargoTypes.includes(ct.name)).map(ct => (
                  <option key={ct.id} value={ct.name}>{ct.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* New: Services Selection */}
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Services Selection *</label>
            <div className="selected-items-container" style={{ flexDirection: 'column', gap: '10px' }}>
              {form.services.map(srvc => (
                <div key={srvc.id} style={{ display: 'flex', flexDirection: 'column', gap: '5px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><strong>{srvc.type}:</strong> {srvc.classification}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: '#2e7d32', fontWeight: 'bold' }}>
                        <input 
                          type="checkbox" 
                          checked={srvc.showPacking || false} 
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const newServices = form.services.map(s => s.id === srvc.id ? { ...s, showPacking: checked } : s);
                            setForm(f => {
                              const updatedPacking = (!checked && srvc.selectedPackingId) 
                                ? f.packingItems.filter(p => p.id !== srvc.selectedPackingId) 
                                : f.packingItems;
                              return { ...f, services: newServices, packingItems: updatedPacking };
                            });
                          }} 
                        /> Add Packing
                      </label>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => {
                          setForm(f => {
                            const updatedServices = f.services.filter(s => s.id !== srvc.id);
                            const updatedPacking = srvc.selectedPackingId
                              ? f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                              : f.packingItems;
                            return { ...f, services: updatedServices, packingItems: updatedPacking };
                          });
                        }}
                      >✕</button>
                    </div>
                  </div>
                  {srvc.showPacking && (
                    <div className="select-field" style={{ marginTop: '5px' }}>
                      {srvc.selectedPackingId ? (
                        <div className="item-chip" style={{ background: '#e2e8f0', justifyContent: 'space-between', display: 'flex', padding: '5px 10px' }}>
                          <span><strong>Packing:</strong> {(() => {
                            const p = packingItems.find(item => item.id == srvc.selectedPackingId);
                            return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                          })()}</span>
                          <button type="button" className="remove-btn" onClick={() => {
                            setForm(f => ({
                              ...f,
                              services: f.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: null } : s),
                              packingItems: f.packingItems.filter(p => p.id !== srvc.selectedPackingId)
                            }));
                          }}>✕</button>
                        </div>
                      ) : (
                        <select value="" onChange={e => {
                          const item = packingItems.find(p => p.id == e.target.value);
                          if (item) {
                            setForm(f => {
                              const updatedServices = f.services.map(s => s.id === srvc.id ? { ...s, selectedPackingId: item.id } : s);
                              const updatedPacking = !f.packingItems.some(p => p.id == item.id) ? [...f.packingItems, item] : f.packingItems;
                              return { ...f, services: updatedServices, packingItems: updatedPacking };
                            });
                          }
                        }}>
                          <option value="">Select Packing Type...</option>
                          {packingItems.filter(p => !form.packingItems.some(selected => selected.id === p.id)).map(p => (
                            <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="select-field" style={{ marginTop: '10px' }}>
              <select value="" onChange={e => handleAutoServiceSelect(e.target.value)}>
                <option value="">+ Add Service Type...</option>
                {uniqueServiceTypes
                  .filter(t => !form.services.some(s => s.type === t))
                  .map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Master Packing Summary */}
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Master Packing Summary</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, minHeight: '35px', padding: '5px', background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
              {form.packingItems.length === 0 && <span className="hint-text">No packing selected</span>}
              {form.packingItems.map(item => (
                <span
                  key={item.id}
                  style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', fontSize: '11px' }}
                >
                  {item.type}{item.classification ? ` - ${item.classification}` : ''}
                  <button
                    onClick={() => setForm(f => ({ ...f, packingItems: f.packingItems.filter(p => p.id !== item.id) }))}
                    style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                  >×</button>
                </span>
              ))}
            </div>
          </div>

          {/* New: Build-up Selection */}
          <div className="field">
            <label>Build-up Selection *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {form.buildUpItems.map(item => (
                <span key={item.id} style={{ background: '#c8dbb8', borderRadius: 16, padding: '2px 8px', display: 'inline-flex', alignItems: 'center' }}>
                  {item.name}
                  <button
                    onClick={() => setForm(f => ({ ...f, buildUpItems: f.buildUpItems.filter(b => b.id !== item.id) }))}
                    style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 'bold' }}
                  >×</button>
                </span>
              ))}
            </div>
            <div className="select-field">
              <select value="" onChange={e => {
                const selectedId = e.target.value;
                const fullObject = buildUpItems.find(b => b.id == selectedId);
                if (fullObject && !form.buildUpItems.some(b => b.id == selectedId)) {
                  setForm(f => ({ ...f, buildUpItems: [...f.buildUpItems, fullObject] }));
                }
              }}>
                <option value="">Select Build-up Item...</option>
                {buildUpItems.filter(b => !form.buildUpItems.some(selected => selected.id === b.id)).map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* House AWBs Section */}
        <div className="field" style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
          <div className="card-header-row">
            <h4 style={{ color: '#1b5e20' }}>House AWBs (Sub-details)</h4>
            <button type="button" className="btn btn-secondary" onClick={addHouseBol}>+ Add House AWB</button>
          </div>
          <div className="selected-items-container" style={{ flexDirection: 'column', background: '#fff' }}>
            {form.homes.map((house, idx) => (
              <div key={house.tempId} className="card" style={{ marginBottom: '15px', border: '1px dashed #2196f3', padding: '20px', background: '#f0f7ff', borderRadius: '8px' }}>
                <div className="card-header-row">
                   <strong style={{ color: '#0d47a1' }}>HOUSE BOL #{idx + 1}</strong>
                   <button type="button" className="btn btn-danger" onClick={() => removeHouseBol(house.tempId)}>Remove</button>
                </div>
                <div className="grid grid-auto small-gap">
                  <div className="field">
                    <label>House Ref *</label>
                    <input type="text" value={house.bolReference} onChange={(e) => {
                      const newHomes = [...form.homes];
                      newHomes[idx].bolReference = e.target.value;
                      setForm(f => ({ ...f, homes: newHomes }));
                    }} />
                  </div>
                  <div className="field">
                    <label>Weight (kg) *</label>
                    <input type="number" value={house.grossMass} onChange={(e) => {
                      const newHomes = [...form.homes];
                      newHomes[idx].grossMass = e.target.value;
                      setForm(f => ({ ...f, homes: newHomes }));
                    }} />
                  </div>
                  <div className="field">
                    <label>Packages *</label>
                    <input type="number" value={house.numberOfPackages} onChange={(e) => {
                      const newHomes = [...form.homes];
                      newHomes[idx].numberOfPackages = e.target.value;
                      setForm(f => ({ ...f, homes: newHomes }));
                    }} />
                  </div>
                  <div className="field" style={{ gridColumn: 'span 2' }}>
                    <label>Goods Description</label>
                    <input type="text" value={house.goodsDescription} onChange={(e) => {
                      const newHomes = [...form.homes];
                      newHomes[idx].goodsDescription = e.target.value;
                      setForm(f => ({ ...f, homes: newHomes }));
                    }} />
                  </div>
                </div>

                <div className="grid grid-auto small-gap" style={{ gridColumn: '1 / -1' }}>
                  <div className="field">
                    <label>House Build-up</label>
                    <div className="selected-items-container">
                      {house.buildUpItems?.map(b => (
                        <div key={b.id} className="item-chip" style={{ background: '#fff' }}>
                          <small>{b.name}</small>
                          <button type="button" className="remove-btn" onClick={() => {
                            const newHomes = [...form.homes];
                            newHomes[idx].buildUpItems = house.buildUpItems.filter(item => item.id !== b.id);
                            setForm(f => ({ ...f, homes: newHomes }));
                          }}>✕</button>
                        </div>
                      ))}
                    </div>
                    <div className="select-field">
                      <select value="" onChange={e => {
                        const item = buildUpItems.find(b => b.id == e.target.value);
                        if (item) {
                          const newHomes = [...form.homes];
                          newHomes[idx].buildUpItems = [...(house.buildUpItems || []), item];
                          setForm(f => ({ ...f, homes: newHomes }));
                        }
                      }}>
                        <option value="">+ Add Build-up...</option>
                        {buildUpItems.filter(b => !house.buildUpItems?.some(hi => hi.id === b.id)).map(b => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* House Services & Packing */}
                <div className="grid grid-auto small-gap" style={{ marginTop: '10px', gridColumn: '1 / -1' }}>
                  <div className="field">
                    <label>House Services & Packing</label>
                    <div className="selected-items-container" style={{ flexDirection: 'column', gap: '8px' }}>
                      {house.services.map(srv => (
                        <div key={srv.id} style={{ background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd', width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <small><strong>{srv.type}:</strong> {srv.classification}</small>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: '#0288d1', cursor: 'pointer' }}>
                                <input 
                                  type="checkbox" 
                                  checked={srv.showPacking || false} 
                                  onChange={(e) => {
                                      const checked = e.target.checked;
                                      setForm(f => {
                                        const newHomes = [...f.homes];
                                        newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, showPacking: checked } : s);
                                        if (!checked && srv.selectedPackingId) {
                                          newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                        }
                                        return { ...f, homes: newHomes };
                                      });
                                  }} 
                                /> Add Packing
                              </label>
                              <button type="button" className="remove-btn" onClick={() => {
                                  setForm(f => {
                                    const newHomes = [...f.homes];
                                    newHomes[idx].services = house.services.filter(s => s.id !== srv.id);
                                    if (srv.selectedPackingId) {
                                      newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                    }
                                    return { ...f, homes: newHomes };
                                  });
                              }}>✕</button>
                            </div>
                          </div>
                          {srv.showPacking && (
                            <div className="select-field" style={{ marginTop: '5px' }}>
                                {srv.selectedPackingId ? (
                                  <div className="item-chip" style={{ background: '#f1f5f9', justifyContent: 'space-between', display: 'flex', padding: '4px 8px' }}>
                                    <small><strong>Packing:</strong> {(() => {
                                      const p = packingItems.find(item => item.id == srv.selectedPackingId);
                                      return p ? `${p.type}${p.classification ? ` - ${p.classification}` : ''}` : '';
                                    })()}</small>
                                    <button type="button" className="remove-btn" onClick={() => {
                                      const newHomes = [...form.homes];
                                      newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: null } : s);
                                      newHomes[idx].packingItems = house.packingItems.filter(p => p.id !== srv.selectedPackingId);
                                      setForm(f => ({ ...f, homes: newHomes }));
                                    }}>✕</button>
                                  </div>
                                ) : (
                                  <select value="" onChange={e => {
                                    const item = packingItems.find(p => p.id == e.target.value);
                                    if (item) {
                                      const newHomes = [...form.homes];
                                      newHomes[idx].services = house.services.map(s => s.id === srv.id ? { ...s, selectedPackingId: item.id } : s);
                                      if (!house.packingItems.some(p => p.id == item.id)) {
                                        newHomes[idx].packingItems = [...house.packingItems, item];
                                      }
                                      setForm(f => ({ ...f, homes: newHomes }));
                                    }
                                  }}>
                                    <option value="">Select House Packing...</option>
                                    {packingItems.filter(p => !house.packingItems.some(hi => hi.id === p.id)).map(p => (
                                      <option key={p.id} value={p.id}>{p.type} - {p.classification}</option>
                                    ))}
                                  </select>
                                )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="select-field" style={{ marginTop: '8px' }}>
                      <select value="" onChange={e => handleHouseAutoServiceSelect(idx, e.target.value)}>
                        <option value="">+ Add Service Type to House...</option>
                        {uniqueServiceTypes
                          .filter(t => !house.services.some(s => s.type === t))
                          .map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="field">
                    <label>House Packing Summary</label>
                    <div className="selected-items-container">
                      {house.packingItems.map(p => (
                        <div key={p.id} className="item-chip" style={{ background: '#fff' }}>
                          <small>{p.type}{p.classification ? ` - ${p.classification}` : ''}</small>
                          <button type="button" className="remove-btn" onClick={() => {
                            const newHomes = [...form.homes];
                            newHomes[idx].packingItems = house.packingItems.filter(item => item.id !== p.id);
                            setForm(f => ({ ...f, homes: newHomes }));
                          }}>✕</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="actions-row">
          <button className="btn btn-primary" onClick={submitApplication}>
            Submit Application
          </button>
        </div>
      </div>

      {submittedBols.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Your Submitted Applications</h3>
            <button className="btn btn-secondary" onClick={loadSubmittedBols} style={{ height: '38px' }}>
              Refresh
            </button>
          </div>
          <table className="grid-table">
            <thead>
              <tr>
                <th>BOL Reference</th>
                <th>Operator</th>
                <th>Status</th>
                <th>Houses</th>
                <th>Flag</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {submittedBols.filter(b => !b.parent_bol_id).map(bol => (
                <tr key={bol.id}>
                  <td>{bol.bol_reference}</td>
                  <td>{bol.owner_operator}</td>
                  <td>
                    <span className={`badge ${bol.status === 'operator_review' ? 'badge-val' : 'badge-mail'}`}>
                      {bol.status === 'operator_review' ? '⚠️ Revision Required' : '⏳ Awaiting Services'}
                    </span>
                  </td>
                  <td>{(bol.homes || []).length}</td>
                  <td>
                    {bol.returned_for_review == 1 ? 'Returned' : '—'}
                  </td>
                  <td>
                    {(bol.status === 'operator_review' || bol.returned_for_review == 1) && (
                      <button className="btn btn-warning" onClick={() => editReturnedBol(bol)}>Edit & Resubmit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

};

export default OperatorApplicationForm;
